/**
 * CU-14ygtkj98p4 (HI_015_006105): bounded contract for the operator-requested IHI revalidation offered after a
 * family-name, sex or date-of-birth change. Both parsers fail closed and rebuild their result; nothing here logs.
 */

export const CORE_DEMOGRAPHIC_REVALIDATION_REQUIREMENT_ID = "HI_015_006105";

export const CORE_DEMOGRAPHIC_REVALIDATION_RESPONSE_KEY = "core_demographic_ihi_revalidation";

const CORE_DEMOGRAPHICS = ["family_name", "sex", "date_of_birth"] as const;

export type CoreDemographic = (typeof CORE_DEMOGRAPHICS)[number];

export interface CoreDemographicRevalidationOffer {
	changedDemographics: CoreDemographic[];
}

const UNCHANGED_RECORD_OUTCOMES = [
	"current_ihi_confirmed",
	"different_ihi_returned",
	"no_match",
	"multiple_match",
	"revalidation_rejected",
	"revalidation_unavailable",
	"indeterminate",
	"resolved_ihi_superseded",
	"resolved_ihi_transition_incomplete",
] as const;

// CU-14ygtkj98p8 (HI_015_016813): the only outcomes that changed the patient's current IHI.
const CHANGED_RECORD_OUTCOMES = ["resolved_ihi_replaced", "resolved_ihi_not_accepted"] as const;

export type CoreDemographicRevalidationOutcome =
	| (typeof UNCHANGED_RECORD_OUTCOMES)[number]
	| (typeof CHANGED_RECORD_OUTCOMES)[number];

const NUMBER_STATUSES = ["active", "deceased", "retired", "expired", "resolved"] as const;
const RECORD_STATUSES = ["verified", "unverified", "provisional"] as const;

export interface CoreDemographicRevalidationResult {
	outcome: CoreDemographicRevalidationOutcome;
	numberStatus: string | null;
	recordStatus: string | null;
	patientRecordChanged: boolean;
}

export interface RevalidationFeedback {
	severity: "success" | "info" | "warning" | "error";
	title: string;
	detail: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const inList = <T extends string>(list: readonly T[], value: unknown): value is T =>
	(list as readonly unknown[]).includes(value);

const boundedLabel = (list: readonly string[], value: unknown): string | null | undefined => {
	if (value === null) return null;
	return inList(list, value) ? value : undefined;
};

/** The same rule the server enforces: an IHI is held and its server-confirmed record status is Verified. */
export const hasServerConfirmedVerifiedIhi = (
	ihi: { ihi_number?: unknown; ihi_record_status?: unknown } | null | undefined
): boolean => {
	const ihiNumber = ihi?.ihi_number;
	const recordStatus = ihi?.ihi_record_status;
	const ihiPresent =
		(typeof ihiNumber === "number" && Number.isFinite(ihiNumber)) || (typeof ihiNumber === "string" && ihiNumber.trim() !== "");

	return ihiPresent && typeof recordStatus === "string" && recordStatus.trim().toLowerCase() === "verified";
};

export const parseCoreDemographicRevalidationOffer = (value: unknown): CoreDemographicRevalidationOffer | null => {
	if (!isRecord(value)) return null;
	if (value.requirement_id !== CORE_DEMOGRAPHIC_REVALIDATION_REQUIREMENT_ID) return null;
	if (value.revalidation_available !== true || value.automatic !== false) return null;

	const changed = value.changed_demographics;
	if (!Array.isArray(changed) || changed.length === 0) return null;
	if (!changed.every((item) => inList(CORE_DEMOGRAPHICS, item))) return null;

	return { changedDemographics: CORE_DEMOGRAPHICS.filter((item) => changed.includes(item)) };
};

/** A later qualifying save widens the pending offer; an unrelated save never clears it. */
export const mergeCoreDemographicRevalidationOffers = (
	current: CoreDemographicRevalidationOffer | null,
	next: CoreDemographicRevalidationOffer
): CoreDemographicRevalidationOffer => ({
	changedDemographics: CORE_DEMOGRAPHICS.filter(
		(item) => next.changedDemographics.includes(item) || !!current?.changedDemographics.includes(item)
	),
});

export const parseCoreDemographicRevalidationResult = (value: unknown): CoreDemographicRevalidationResult | null => {
	if (!isRecord(value)) return null;
	if (value.requirement_id !== CORE_DEMOGRAPHIC_REVALIDATION_REQUIREMENT_ID) return null;

	const outcome = value.outcome;
	if (!inList(CHANGED_RECORD_OUTCOMES, outcome) && !inList(UNCHANGED_RECORD_OUTCOMES, outcome)) return null;
	const changed = inList(CHANGED_RECORD_OUTCOMES, outcome);
	if (value.patient_record_changed !== changed) return null;

	const numberStatus = boundedLabel(NUMBER_STATUSES, value.number_status);
	const recordStatus = boundedLabel(RECORD_STATUSES, value.record_status);
	if (numberStatus === undefined || recordStatus === undefined) return null;

	return { outcome, numberStatus, recordStatus, patientRecordChanged: changed };
};

/** CU-14ygtkj98p8: a response the parser rejects may still say the record changed, which must still trigger a refresh. */
export const responseReportsPatientRecordChanged = (value: unknown): boolean =>
	isRecord(value) && value.patient_record_changed === true;

const DEMOGRAPHIC_LABELS: Record<CoreDemographic, string> = {
	family_name: "family name",
	sex: "sex",
	date_of_birth: "date of birth",
};

export const describeChangedDemographics = (offer: CoreDemographicRevalidationOffer): string => {
	const labels = offer.changedDemographics.map((item) => DEMOGRAPHIC_LABELS[item]);
	const joined = labels.length > 1 ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}` : labels[0];
	return joined.charAt(0).toUpperCase() + joined.slice(1);
};

const titleCase = (label: string): string => label.charAt(0).toUpperCase() + label.slice(1);

const statusSummary = (result: CoreDemographicRevalidationResult): string => {
	const parts = [
		result.numberStatus ? `number status ${titleCase(result.numberStatus)}` : null,
		result.recordStatus ? `record status ${titleCase(result.recordStatus)}` : null,
	].filter(Boolean);
	return parts.length > 0 ? ` (${parts.join(", ")})` : "";
};

const UNCHANGED = "The stored IHI and its statuses have not been changed.";

export const describeCoreDemographicRevalidationResult = (result: CoreDemographicRevalidationResult | null): RevalidationFeedback => {
	switch (result?.outcome) {
		case "current_ihi_confirmed":
			return { severity: "success", title: "IHI revalidated", detail: `The HI Service returned the same IHI${statusSummary(result)}. ${UNCHANGED}` };
		case "different_ihi_returned":
			return { severity: "warning", title: "A different IHI was returned", detail: `The HI Service returned a different IHI${statusSummary(result)}. ${UNCHANGED} Review the patient's identity before taking further action.` };
		case "no_match":
			return { severity: "warning", title: "No matching IHI found", detail: `No IHI matched the patient's current details. ${UNCHANGED} Check the family name, sex and date of birth with the patient.` };
		case "multiple_match":
			return { severity: "warning", title: "IHI could not be confirmed", detail: `The HI Service did not return a single IHI. ${UNCHANGED}` };
		case "revalidation_rejected":
			return { severity: "warning", title: "Revalidation not sent", detail: `Revalidation needs the patient's saved family name, given name (unless one name only), sex and date of birth. ${UNCHANGED}` };
		case "revalidation_unavailable":
			return { severity: "error", title: "IHI revalidation unavailable", detail: `The HI Service could not be reached. ${UNCHANGED} Try again later.` };
		case "resolved_ihi_replaced":
			return { severity: "success", title: "Resolved IHI replaced", detail: `The HI Service reported this patient's IHI as resolved to a different IHI. The replacement was validated against the patient's saved details and is now the current IHI${statusSummary(result)}. The previous IHI is kept in the IHI history with a Resolved status.` };
		case "resolved_ihi_not_accepted":
			return { severity: "warning", title: "Resolved IHI — replacement not accepted", detail: "The HI Service reported this patient's IHI as resolved to a different IHI, but the replacement could not be validated against the patient's saved details and was not stored. The previous IHI has been moved to the IHI history with a Resolved status, so the patient now has no current IHI. An alert has been raised in HI Reviews for operator action." };
		case "resolved_ihi_superseded":
			return { severity: "warning", title: "Revalidation result not applied", detail: "The patient record changed while the revalidation was in progress, so this result was not applied and this request changed nothing. Review the current record and request revalidation again if needed." };
		case "resolved_ihi_transition_incomplete":
			return { severity: "error", title: "IHI update not completed", detail: `The HI Service reported this patient's IHI as resolved, but the update could not be completed. ${UNCHANGED} Request revalidation again.` };
		default:
			return { severity: "warning", title: "IHI could not be confirmed", detail: `The revalidation result could not be confirmed. ${UNCHANGED}` };
	}
};

export const UNREADABLE_CHANGED_RECORD_FEEDBACK: RevalidationFeedback = {
	severity: "warning",
	title: "IHI revalidation changed the record",
	detail: "The revalidation changed the patient's IHI, but its result could not be read. The patient's current IHI has been refreshed; review it and check HI Reviews.",
};

export const describeCoreDemographicRevalidationError = (error: unknown): RevalidationFeedback => {
	const status = isRecord(error) && isRecord(error.response) ? error.response.status : undefined;

	if (status === 409) {
		return { severity: "warning", title: "Revalidation not available", detail: "IHI revalidation is only available for a patient with a verified current IHI." };
	}

	return { severity: "error", title: "Revalidation could not be requested", detail: `${UNCHANGED} Try again later.` };
};
